/**
 * Generic custom storage adapter for Zustand persist middleware that applies
 * filter functions on read/write operations to localStorage.
 *
 * This allows you to transform or filter state before persisting and after hydrating,
 * useful for cleaning up stale data, limiting cache size, etc.
 *
 * @template T - The type of the state being persisted
 * @param filterFn - Function that takes the state and returns a filtered/transformed version
 * @returns A storage adapter compatible with Zustand's persist middleware
 *
 * @example
 * ```typescript
 * const store = create<MyStore>()(
 *   persist(
 *     (set, get) => ({ ... }),
 *     {
 *       name: "my-storage",
 *       storage: createFilteredStorage<Pick<MyStore, "data">>(
 *         (state) => ({
 *           ...state,
 *           data: filterOldData(state.data),
 *         })
 *       ),
 *       partialize: (state) => ({ data: state.data }),
 *     }
 *   )
 * );
 * ```
 */
export const createFilteredStorage = <T>(filterFn: (state: T) => T) => {
  return {
    getItem: (name: string) => {
      const str = localStorage.getItem(name);
      if (!str) return null;

      try {
        const parsed = JSON.parse(str);
        if (parsed.state) {
          parsed.state = filterFn(parsed.state);
        }
        return parsed;
      } catch (e) {
        console.error("Error parsing stored data:", e);
        return null;
      }
    },
    setItem: (name: string, value: any) => {
      try {
        const valueToStore = { ...value };
        if (valueToStore.state) {
          valueToStore.state = filterFn(valueToStore.state);
        }
        localStorage.setItem(name, JSON.stringify(valueToStore));
      } catch (e) {
        console.error("Error storing data:", e);
      }
    },
    removeItem: (name: string) => {
      localStorage.removeItem(name);
    },
  };
};

/**
 * Creates a throttled localStorage storage adapter for Zustand persist middleware.
 *
 * Writes are throttled to reduce localStorage thrashing during rapid state updates.
 * The first write happens immediately, subsequent writes are batched until the
 * throttle interval passes. A beforeunload listener ensures pending writes are
 * flushed before page exit.
 *
 * @param storageKey - The localStorage key used for this store
 * @param throttleMs - Throttle interval in milliseconds (default: 3000)
 * @returns A storage adapter compatible with Zustand's persist middleware
 *
 * @example
 * ```typescript
 * const store = create<MyStore>()(
 *   persist(
 *     (set, get) => ({ ... }),
 *     {
 *       name: "my-storage",
 *       storage: createJSONStorage(() => createThrottledStorage("my-storage", 3000)),
 *     }
 *   )
 * );
 * ```
 */
export function createThrottledStorage(storageKey: string, throttleMs = 3000) {
  let pendingWrite: string | null = null;
  let lastWriteTime = 0;
  let throttleTimeout: ReturnType<typeof setTimeout> | null = null;

  function flushPendingWrite() {
    if (pendingWrite !== null) {
      try {
        localStorage.setItem(storageKey, pendingWrite);
        lastWriteTime = Date.now();
      } catch (e) {
        console.error(
          `[throttledStorage:${storageKey}] Failed to flush to localStorage:`,
          e,
        );
      }
      pendingWrite = null;
    }
    if (throttleTimeout) {
      clearTimeout(throttleTimeout);
      throttleTimeout = null;
    }
  }

  // Flush on page exit
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", flushPendingWrite);
  }

  return {
    getItem: (name: string): string | null => {
      // If there's a pending write for this key, return that (most recent state)
      if (pendingWrite !== null && name === storageKey) {
        return pendingWrite;
      }
      return localStorage.getItem(name);
    },
    setItem: (name: string, value: string): void => {
      const now = Date.now();
      const timeSinceLastWrite = now - lastWriteTime;

      if (timeSinceLastWrite >= throttleMs) {
        // Enough time has passed, write immediately
        try {
          localStorage.setItem(storageKey, value);
          lastWriteTime = now;
        } catch (e) {
          console.error(
            `[throttledStorage:${storageKey}] Failed to write to localStorage:`,
            e,
          );
        }
        pendingWrite = null;
        if (throttleTimeout) {
          clearTimeout(throttleTimeout);
          throttleTimeout = null;
        }
      } else {
        // Store for later and schedule write at end of throttle interval
        pendingWrite = value;
        if (!throttleTimeout) {
          const remainingTime = throttleMs - timeSinceLastWrite;
          throttleTimeout = setTimeout(() => {
            flushPendingWrite();
          }, remainingTime);
        }
      }
    },
    removeItem: (name: string): void => {
      // Clear pending write if removing this key
      if (name === storageKey) {
        pendingWrite = null;
        if (throttleTimeout) {
          clearTimeout(throttleTimeout);
          throttleTimeout = null;
        }
      }
      localStorage.removeItem(name);
    },
  };
}
